/**
 * AI search telemetry: the `Search` event family and the abandonment ledger.
 *
 * graft/A6: extracted verbatim from AiSearch.tsx so the routed full-page
 * surface emits the SAME events with the SAME correlation ids. None of this
 * depends on how results are painted — it depends only on the hook's state and
 * the visible ranked list — which is exactly why it can move.
 *
 * The surface still owns `LinkClick` (see useAiSearchNavigation) because the
 * click payload needs the surface's notion of which list a row came from.
 */
import { useCallback, useEffect, useRef } from "react";

import type { useTracking } from "utilities/analytics/analytics";

import { normalizeKey } from "../entityMeta";
import type { AiSearchPhase, IAiSearchState, ICategorizedHit } from "../types";

/**
 * `trackEvent` from the surface's own `useTracking({ component: "AI Search" })`.
 * Passed in rather than called here so a component using several of these hooks
 * still creates exactly ONE react-tracking context, as before the extraction.
 */
export type TrackEvent = ReturnType<typeof useTracking>["trackEvent"];

/**
 * Cap on the number of ranked results serialized into the "Search" telemetry
 * event. Keeps the payload well under Sawmill's per-event size limit while still
 * capturing the full visible result set for relevance analysis.
 */
export const MAX_LOGGED_RESULTS = 50;

export interface ISearchOutcome {
    searchId: string;
    traceId: string | null;
    phase: AiSearchPhase;
    query: string;
    resultCount: number;
    clicked: boolean;
}

export type SearchTrackPhase = "first-results" | "complete" | "interrupted";

interface IUseAiSearchTelemetryArgs {
    state: IAiSearchState;
    /** Visible count: committed rows plus appended rows. */
    resultCount: number;
    /** The full visible list in painted order — committed rows then appended. */
    navigableResults: ICategorizedHit[];
    trackEvent: TrackEvent;
}

export interface IAiSearchTelemetry {
    /** Emit a `Search` event for the current search at the given phase. */
    trackSearch: (searchPhase: SearchTrackPhase) => void;
    /**
     * Emit an abandonment event for the search currently in the outcome
     * snapshot. Safe to call from any exit path — the dedup ledger guarantees
     * at most one abandonment per searchId.
     */
    abandonCurrent: () => void;
    /** Mark the current search as clicked so it is never counted as abandoned. */
    markClicked: () => void;
    /** Live outcome snapshot, exposed for tests and diagnostics. */
    outcomeRef: React.MutableRefObject<ISearchOutcome>;
}

export function useAiSearchTelemetry(args: IUseAiSearchTelemetryArgs): IAiSearchTelemetry {
    const { state, resultCount, navigableResults, trackEvent } = args;

    // Snapshot of the most recent search's outcome, kept in a ref so abandonment
    // can be reported from unmount/close/navigation handlers without stale closures.
    const searchOutcomeRef = useRef<ISearchOutcome>({
        searchId: "",
        traceId: null,
        phase: "idle",
        query: "",
        resultCount: 0,
        clicked: false,
    });
    // searchIds already reported as abandoned, so close + unmount + next-search
    // can't emit duplicate abandonment events for the same search.
    const abandonReportedRef = useRef<Set<string>>(new Set());
    const lastTrackedPhaseRef = useRef<string | null>(null);

    // Emit an "abandoned" search event when a completed search (results shown or
    // empty) is left without the user clicking a result. Guarded so the same
    // searchId is reported at most once across the close/unmount/next-search paths.
    const emitAbandon = useCallback(
        (snap: ISearchOutcome) => {
            if (!snap.searchId || snap.clicked) return;
            if (snap.phase !== "done" && snap.phase !== "empty") return;
            if (abandonReportedRef.current.has(snap.searchId)) return;
            abandonReportedRef.current.add(snap.searchId);
            trackEvent({
                event: "Search",
                extraInfo: {
                    searchId: snap.searchId,
                    // Same null-safe trace correlation as click events, so an
                    // abandoned search joins to its server span the same way
                    // a clicked one does (abandonment is the negative signal).
                    traceId: snap.traceId,
                    query: snap.query,
                    resultCount: snap.resultCount,
                    searchPhase: "abandoned",
                },
            });
        },
        [trackEvent],
    );

    // Hold the latest emitAbandon so the unmount-only cleanup can call it without
    // depending on its identity. Listing emitAbandon as a dependency there would
    // make the cleanup fire whenever its identity changes (e.g. if trackEvent is
    // recreated), emitting a spurious abandonment while the user is still viewing
    // results.
    const emitAbandonRef = useRef(emitAbandon);
    useEffect(() => {
        emitAbandonRef.current = emitAbandon;
    }, [emitAbandon]);

    const abandonCurrent = useCallback(() => {
        emitAbandon(searchOutcomeRef.current);
    }, [emitAbandon]);

    const markClicked = useCallback(() => {
        searchOutcomeRef.current.clicked = true;
    }, []);

    // Keep searchOutcomeRef current, and report abandonment when one search is
    // superseded by another (query reformulation) without an intervening click.
    // Uses state.searchQuery (the query that triggered the search), not the live
    // input, so a deferred abandonment event reports the original query rather
    // than whatever the user has since typed.
    useEffect(() => {
        const prev = searchOutcomeRef.current;
        if (prev.searchId && prev.searchId !== state.searchId) {
            emitAbandon(prev);
        }
        searchOutcomeRef.current = {
            searchId: state.searchId,
            traceId: state.traceId,
            phase: state.phase,
            query: state.searchQuery,
            resultCount,
            clicked: prev.searchId === state.searchId ? prev.clicked : false,
        };
    }, [state.searchId, state.traceId, state.phase, state.searchQuery, resultCount, emitAbandon]);

    const trackSearch = useCallback(
        (searchPhase: SearchTrackPhase) => {
            const durationMs =
                state.searchStartTime != null ? Date.now() - state.searchStartTime : undefined;
            trackEvent({
                event: "Search",
                extraInfo: {
                    searchId: state.searchId,
                    // Null-safe server-span correlation, same as click events
                    // (null when the trace SSE event hasn't arrived yet).
                    traceId: state.traceId,
                    // The query that triggered this search, not the live input —
                    // consistent with the abandonment path, so a "complete" event
                    // can't log a newly-typed query against the old search's id.
                    query: state.searchQuery,
                    resultCount,
                    // Total hits returned before client-side filtering (excluded
                    // entities + unroutable hits), spanning committed AND appended
                    // sections so raw >= visible always holds (review 1511772).
                    // resultCount is the visible count.
                    rawResultCount: state.results.length + state.appendedResults.length,
                    searchPhase,
                    durationMs,
                    // Parsed intent (entity targeting + question detection).
                    entityScope: state.entityScope,
                    scopedEntities: [...state.scopedEntities],
                    isQuestion: state.isQuestion,
                    // The full ranked result list the user is shown (committed +
                    // appended, in visible order). Titles are intentionally
                    // omitted — summaries can contain customer PII; ids + scores
                    // are enough to reconstruct ranking quality.
                    results: navigableResults.slice(0, MAX_LOGGED_RESULTS).map((hit, i) => ({
                        rank: i + 1,
                        entity: normalizeKey(hit.entity),
                        id: hit.id,
                        score: hit.score,
                        rerankScore: hit.rerank_score,
                        reranker: hit.reranker,
                        modalities: [...new Set(hit.sourceOps.map((op) => op.modality))],
                    })),
                    // Per-operation breakdown: which sub-queries ran, their modality,
                    // how many hits each returned, and how long each took.
                    operations: state.operations.map((op) => ({
                        id: op.id,
                        entity: op.entity,
                        modality: op.modality,
                        stage: op.stage,
                        status: op.status,
                        resultCount: op.resultCount,
                        elapsedMs: op.elapsed_ms,
                    })),
                    qaAnswerSources: Object.keys(state.qaAnswers),
                },
            });
        },
        [
            trackEvent,
            resultCount,
            navigableResults,
            state.searchId,
            state.traceId,
            state.searchQuery,
            state.searchStartTime,
            state.results.length,
            state.appendedResults.length,
            state.entityScope,
            state.scopedEntities,
            state.isQuestion,
            state.operations,
            state.qaAnswers,
        ],
    );

    // lastTrackedPhaseRef prevents duplicate events when trackSearch changes
    useEffect(() => {
        if (state.phase === "streaming" && lastTrackedPhaseRef.current !== "streaming") {
            lastTrackedPhaseRef.current = "streaming";
            trackSearch("first-results");
        } else if (
            (state.phase === "done" || state.phase === "empty") &&
            lastTrackedPhaseRef.current !== state.phase
        ) {
            lastTrackedPhaseRef.current = state.phase;
            trackSearch("complete");
        } else if (state.phase === "loading" || state.phase === "idle") {
            lastTrackedPhaseRef.current = null;
        }
    }, [state.phase, trackSearch]);

    // Emit a final abandonment on unmount ONLY. Empty deps + reading
    // emitAbandon through a ref keep this from firing mid-session when a
    // dependency's identity changes.
    useEffect(() => {
        return () => {
            emitAbandonRef.current(searchOutcomeRef.current);
        };
    }, []);

    return { trackSearch, abandonCurrent, markClicked, outcomeRef: searchOutcomeRef };
}
